import {
  TWorktimeWorkHoursDataDays,
  TWorktimeWorkHoursDataHours,
} from '@infotech-soe/merps/types/WorkTime';
import { TDatabaseId } from '@infotech-soe/metaschema';
import { endOfMonth, getDaysInMonth } from 'date-fns';

import IApiProvider from '../../infrastructure/providers/abstract/IApiProvider';
import MerpsApiProvider from '../../infrastructure/providers/pb-adapter/MerpsApiProvider';
import { WorkDay, WorkTableData } from '../../models/work-table/WorkTableData';
import { WorkTableType } from '../commands/UserInteractor';

import IWorkTableService from './IWorkTableService';

export class WorkTableServiceSingleton implements IWorkTableService {
  private readonly apiProvider: IApiProvider;
  constructor() {
    this.apiProvider = new MerpsApiProvider();
  }

  public update(
    postId: TDatabaseId,
    date: Date,
    hours: WorkTableData['workHours'],
    days: WorkTableData['workDays'],
    tableType: WorkTableType
  ): Promise<void> {
    const processedHours = this.processWorkingHours(hours);
    const processedDays = this.processWorkingDays(days);

    return tableType === WorkTableType.CALENDAR
      ? this.workTimesApi.updateTimeSheet(postId, processedHours, processedDays)
      : tableType === WorkTableType.TIME_BOARD
        ? this.workTimesApi.updateTimeBoard(postId, hours, processedDays)
        : this.workTimesApi.updateWorkTimes(
            postId,
            date,
            processedHours,
            processedDays,
            tableType === WorkTableType.PLAN
          );
  }

  public loadPlanWorkHours(
    workerId: TDatabaseId,
    newDate?: { month: number; year: number }
  ): Promise<WorkTableData> {
    return this.loadWorkHours(workerId, true, newDate);
  }

  public loadFactWorkHours(
    workerId: TDatabaseId,
    newDate?: { month: number; year: number }
  ): Promise<WorkTableData> {
    return this.loadWorkHours(workerId, false, newDate);
  }

  public loadTimeSheet(timeSheetId: TDatabaseId): Promise<WorkTableData> {
    return this.workTimesApi.getTimeSheet(timeSheetId);
  }

  public loadTimeBoard(workerId: TDatabaseId): Promise<WorkTableData> {
    return this.workTimesApi.getTimeBoard(workerId);
  }

  private async loadWorkHours(
    workerId: TDatabaseId,
    plan: boolean,
    newDate?: { month: number; year: number }
  ): Promise<WorkTableData> {
    const startDate =
      newDate !== undefined
        ? new Date(newDate.year, newDate.month, 1)
        : await this.monthStart();
    const endDate =
      newDate !== undefined
        ? new Date(newDate.year, newDate.month + 1, 0)
        : endOfMonth(startDate);
    const workTimes = await this.workTimesApi.getWorkTimes(
      workerId,
      startDate,
      endDate,
      plan
    );

    // some days may not be filled earlier or not exist on server
    // so we need to fill them
    workTimes.workDays = this.processServerWorkDays(
      workTimes.workDays,
      getDaysInMonth(startDate)
    );

    return {
      ...workTimes,
      startDate,
      endDate,
    };
  }

  private processServerWorkDays(
    days: WorkTableData['workDays'],
    daysCount: number
  ): WorkTableData['workDays'] {
    if (days.length === daysCount) {
      return days;
    }

    const processedDays: WorkTableData['workDays'] = [];

    for (let i = 1; i <= daysCount; i++) {
      const day = days.find(d => d.day === i);
      if (day) {
        processedDays.push(day);
        continue;
      }

      const newDay: WorkDay = {
        day: i,
        dayTypeCode: null,
        isAbsence: false,
        isRestricted: false,
        color: '',
      };

      processedDays.push(newDay);
    }

    return processedDays;
  }

  private processWorkingHours(
    hours: WorkTableData['workHours']
  ): TWorktimeWorkHoursDataHours {
    return Object.entries(hours).flatMap(([type, val]) =>
      val.worksByDay.map(day => ({
        type,
        day: day.day,
        hours: day.hours,
      }))
    );
  }

  private readonly processWorkingDays = (
    days: WorkTableData['workDays']
  ): TWorktimeWorkHoursDataDays =>
    days.map(day => ({ day: day.day, type: day.dayTypeCode }));

  private get workTimesApi() {
    return this.apiProvider.workTimes;
  }

  public async monthStart(): Promise<Date> {
    const option = await this.apiProvider.options.getOption('SalaryCalcPeriod');

    if (option.value !== null) {
      return new Date(option.value);
    }

    const currentDate = new Date();
    return new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  }
}

const WorkTableService = (() => {
  let instance: IWorkTableService;
  return new Proxy(WorkTableServiceSingleton, {
    construct(Target: typeof WorkTableServiceSingleton): IWorkTableService {
      if (!instance) {
        instance = new Target();
      }
      return instance;
    },
  });
})();

export default WorkTableService;
